// src/pages/verify-email/edit-email.tsx (or wherever your EditEmail lives)
import React, { useEffect, useMemo, useState } from 'react';
import { ContentSection, Heading, SubHeading } from '../../shared.styled';
import { VSpace } from '../../../chat/settings/account/account-settings';
import { VerifyYourEmail } from './verify-email.styled';
import { InstallButton } from '../install/install.styled';
import { EditInput } from '../../../../components/edit-input/edit-input';
import type { OnboardingStepProps } from '../../registration';
import { useAuth } from '../../../../contexts/auth/auth-context';
import { authApi } from '../../../../api';

export const EditEmail: React.FC<OnboardingStepProps> = ({ goNext }) => {
  const { onboarding, setRegistration } = useAuth();
  // source-of-truth: onboarding.email (current verified / current account email)
  const onboardingEmail = useMemo(() => onboarding?.email ?? '', [onboarding?.email]);

  // NEW: typed-but-unverified email (persisted as onboarding.pendingEmail only)
  const onboardingPendingEmail = useMemo(
    () => (onboarding as any)?.pendingEmail ?? '',
    [onboarding]
  );

  // keep it controlled for typing, but always initialized/synced from pendingEmail if present, else from onboarding.email
  const [email, setEmail] = useState(onboardingPendingEmail || onboardingEmail);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    // initialize onboarding with whatever onboarding email is right now
    // ✅ IMPORTANT: do NOT write the typed email into onboarding.email here
    setRegistration({ email: onboardingEmail, authProvider: 'email' });

    // ✅ FIX:
    // If email is prepopulated and user never edits it, we still want VerifyEmail
    // to have a targetEmail (pendingEmail) so the flow can send + verify a code.
    // This does NOT change onboarding.email, it only ensures pendingEmail exists when possible.
    const prefilled = onboardingEmail.trim().toLowerCase();
    if (prefilled && !(onboarding as any)?.pendingEmail) {
      setRegistration({ pendingEmail: prefilled, authProvider: 'email' } as any);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if onboarding email arrives/changes later, sync local + onboarding
  useEffect(() => {
    const next = onboardingPendingEmail || onboardingEmail;
    setEmail(next);

    // ✅ keep canonical email in onboarding.email
    // ✅ keep typed email in onboarding.pendingEmail (if any)
    setRegistration({ email: onboardingEmail, authProvider: 'email' });

    // ✅ FIX:
    // If onboardingEmail hydrates in later (DB/profile load) and user never types,
    // ensure pendingEmail is set so Continue / VerifyEmail has a target.
    const prefilled = onboardingEmail.trim().toLowerCase();
    if (prefilled && !(onboarding as any)?.pendingEmail) {
      setRegistration({ pendingEmail: prefilled, authProvider: 'email' } as any);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboardingEmail, onboardingPendingEmail]);

  // update onboarding when the user edits the email field
  useEffect(() => {
    const trimmed = email.trim().toLowerCase();

    // ✅ store only in pendingEmail (never in email)
    // ✅ if user clears the field, clear pendingEmail too
    setRegistration({
      pendingEmail: trimmed ? trimmed : undefined,
      authProvider: 'email',
    } as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  const handleContinue = async () => {
    // ✅ FIX:
    // If user clicks Continue without editing, always derive the effective email from:
    // 1) the input state (email) if present
    // 2) else fallback to onboardingEmail (prefilled from DB)
    const effective = (email || onboardingEmail).trim().toLowerCase();
    if (!effective) return;

    setLoading(true);
    setErrorMsg(null);

    try {
      /**
       * ONBOARDING OTP FLOW:
       * - User is not yet authenticated
       * - Send OTP for signup with invitation code
       */
      const invitationCode = (onboarding as any)?.invitationCode;
      if (!invitationCode) {
        setErrorMsg('Invitation code is required for onboarding.');
        return;
      }

      // Check email uniqueness before sending OTP
      const emailCheck = await authApi.checkEmailAvailable(effective, invitationCode);
      if (!emailCheck.available) {
        setErrorMsg(emailCheck.reason || 'This email is already in use.');
        return;
      }

      await authApi.signupEmail(effective, invitationCode);

      // ✅ keep it in pendingEmail only (do NOT set onboarding.email here)
      setRegistration({ pendingEmail: effective, authProvider: 'email' } as any);
      goNext();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Edit email error:', error);
      setErrorMsg(
        error.response?.data?.detail || 'Failed to send verification code. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <ContentSection data-testid="ob-emailfunnel">
      <div data-testid="ob-emailfunnel-headings">
        <VSpace $h={32} />
        <Heading data-testid="ob-signup-heading" isLight={false}>
          Judgment. <br />
          Refined.{` `}
        </Heading>
        <VSpace $h={16} />

        <SubHeading data-testid="ob-signup-subheading" isLight={false}>
          The long conversations that create enterprise value.
        </SubHeading>
        <VSpace $h={71} />
      </div>

      <div style={{ width: '100%' }}>
        <VerifyYourEmail>Verify your email to continue</VerifyYourEmail>
        <VSpace $h={32} />

        <EditInput
          placeholder="Enter your email"
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />

        {errorMsg ? (
          <>
            <VSpace $h={12} />
            <div style={{ fontFamily: 'Inter, sans-serif', fontSize: 12, color: '#ff6b6b' }}>
              {errorMsg}
            </div>
          </>
        ) : null}

        <VSpace $h={16} />
        <InstallButton onClick={handleContinue} disabled={loading}>
          {loading ? 'Sending...' : 'Send code'}
        </InstallButton>
      </div>
    </ContentSection>
  );
};

(EditEmail as any).smallLogo = true;
(EditEmail as any).progress = false;
(EditEmail as any).containerAlign = 'flex-start';
(EditEmail as any).containerJustify = 'flex-start';
(EditEmail as any).background = 'linear-gradient(180deg, #222327 0%, #1A1A1C 100%);';
